/**
 * As escolhas da projeção, lembradas por aparelho.
 *
 * Ficam no `localStorage` e não no banco porque a TV é o aparelho: quem liga a
 * TV do salão toda manhã não deveria reconfigurar nada, e a mesma instalação
 * pode ter uma TV com nome do espectador e outra sem. O padrão do intervalo
 * continua vindo de Configurações — aqui ele só é sobreposto.
 *
 * Uma única chave em JSON, e não uma por opção como no legado, porque cinco
 * leituras avulsas viram cinco lugares para o `try/catch` faltar.
 */

/**
 * O que toca por baixo das fotos.
 *
 * `musicas` é a escolha na hora, na ordem em que foram marcadas; `playlist`, uma
 * já montada em Acervo → Músicas. O padrão é nenhuma: o salão costuma ter som
 * próprio, e música sem ninguém pedir é pior que silêncio.
 */
export type EscolhaDeMusica =
  { tipo: "nenhuma" } | { tipo: "playlist"; id: number } | { tipo: "musicas"; ids: number[] };

export interface PreferenciasGaleria {
  /** Legenda com o nome do espectador sobre a foto. */
  mostrarNome: boolean;
  /** Intercalar divulgações entre as fotos. */
  divulgacoes: boolean;
  /** Sobrepõe `galeria_divulgacao_every`; nulo usa o valor de Configurações. */
  intervalo: number | null;
  /** Som na divulgação em vídeo. Nasce desligado: o salão costuma ter o próprio. */
  audio: boolean;
  musica: EscolhaDeMusica;
  /** Embaralhar a cada volta, em vez de seguir a ordem da playlist. */
  musicaAleatoria: boolean;
  /** No fim da lista, começar de novo; sem isto, a galeria segue em silêncio. */
  musicaRepetir: boolean;
}

export const PREFERENCIAS_PADRAO: PreferenciasGaleria = {
  mostrarNome: true,
  divulgacoes: true,
  intervalo: null,
  audio: false,
  musica: { tipo: "nenhuma" },
  musicaAleatoria: false,
  musicaRepetir: true,
};

const CHAVE = "galeria:preferencias";

/** Interface mínima do `localStorage`, para o teste não precisar de navegador. */
export interface Deposito {
  getItem(chave: string): string | null;
  setItem(chave: string, valor: string): void;
}

function depositoPadrao(): Deposito | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Safari em modo privado chega a lançar ao tocar em localStorage.
    return null;
  }
}

export function lerPreferencias(deposito = depositoPadrao()): PreferenciasGaleria {
  if (!deposito) return PREFERENCIAS_PADRAO;

  try {
    const cru = deposito.getItem(CHAVE);
    if (!cru) return PREFERENCIAS_PADRAO;

    const lido = JSON.parse(cru) as Record<string, unknown>;
    return {
      mostrarNome: booleano(lido.mostrarNome, PREFERENCIAS_PADRAO.mostrarNome),
      divulgacoes: booleano(lido.divulgacoes, PREFERENCIAS_PADRAO.divulgacoes),
      intervalo: intervalo(lido.intervalo),
      audio: booleano(lido.audio, PREFERENCIAS_PADRAO.audio),
      musica: escolhaDeMusica(lido.musica),
      musicaAleatoria: booleano(lido.musicaAleatoria, PREFERENCIAS_PADRAO.musicaAleatoria),
      musicaRepetir: booleano(lido.musicaRepetir, PREFERENCIAS_PADRAO.musicaRepetir),
    };
  } catch {
    // JSON estragado não pode impedir a galeria de abrir.
    return PREFERENCIAS_PADRAO;
  }
}

export function gravarPreferencias(p: PreferenciasGaleria, deposito = depositoPadrao()): void {
  try {
    deposito?.setItem(CHAVE, JSON.stringify(p));
  } catch {
    // Cota cheia ou armazenamento bloqueado: a projeção segue com o que está em memória.
  }
}

function booleano(valor: unknown, padrao: boolean): boolean {
  return typeof valor === "boolean" ? valor : padrao;
}

function intervalo(valor: unknown): number | null {
  if (typeof valor !== "number" || !Number.isFinite(valor)) return null;
  const n = Math.floor(valor);
  return n >= 1 && n <= 50 ? n : null;
}

const idValido = (x: unknown): x is number => Number.isInteger(x) && (x as number) > 0;

/** A escolha de música lida de fora — aparelho ou link. Estragada vira nenhuma. */
export function escolhaDeMusica(valor: unknown): EscolhaDeMusica {
  const v = valor as { tipo?: unknown; id?: unknown; ids?: unknown } | null;
  if (v?.tipo === "playlist" && idValido(v.id)) return { tipo: "playlist", id: v.id };
  if (v?.tipo === "musicas" && Array.isArray(v.ids)) {
    const ids = [...new Set(v.ids.filter(idValido))].slice(0, 200);
    if (ids.length > 0) return { tipo: "musicas", ids };
  }
  return { tipo: "nenhuma" };
}
